import { useCallback, useEffect, useRef, useState } from "react";
import ChevronIcon from "../../../../../assets/svg/chevron-icon";
import CheckIcon from "../../../../../assets/svg/check-icon";
import CompletedIcon from "../../../../../assets/svg/completed-icon";
import PlusFilledIcon from "../../../../../assets/svg/plus-filled-icon";
import { visibleScrollbar } from "../../../../../utils/functions/get-visible-scrollbar";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import type { CrmViewItem } from "../../../../../redux/slices/crmLeadListSlice";
import { setDrawer } from "../../../../../redux/slices/drawerSlice";
import { useDispatch } from "react-redux";

const ALL_CONTACTS_VIEW: CrmViewItem = {
  id: "all_leads",
  name: "All Leads",
};

interface CrmViewSelectorDropdownProps {
  crmViews?: CrmViewItem[];
  selectedViewId?: string;
  onSelectView?: (viewId: string) => void;
  onCreateView?: () => void;
}

export const CrmViewSelectorDropdown = ({
  crmViews = [],
  selectedViewId = "all_leads",
  onSelectView,
  onCreateView,
}: CrmViewSelectorDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const allViews: CrmViewItem[] = [ALL_CONTACTS_VIEW, ...crmViews];
  const primarySelected: CrmViewItem =
    allViews?.find((v) => v?.id === selectedViewId) ?? ALL_CONTACTS_VIEW;

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelectView = useCallback(
    (viewId: string) => {
      onSelectView?.(viewId);
      setIsOpen(false);
    },
    [onSelectView]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef?.current &&
        !dropdownRef?.current?.contains(event?.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="min-w-[180px] flex items-center justify-between gap-[4px] text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap border hover:bg-hover-effect dark:hover:bg-hover-effect-dk border-border-gray dark:border-border-gray-dk rounded-[6px] px-[9px] py-[8px]"
      >
        <div className="flex items-center gap-[4px]">
          {primarySelected?.id === "all_leads" ? (
            <CompletedIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
          ) : (
            primarySelected?.emoji_icon && (
              <span className="text-2xl leading-none shrink-0">
                {primarySelected.emoji_icon}
              </span>
            )
          )}
          <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
            {primarySelected?.name}
          </span>
        </div>
        <ChevronIcon
          className={`w-[18px] h-[18px] shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-10 right-0 z-50 bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[20px] border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect p-3 flex flex-col gap-[18px]">
          <div className="flex gap-3">
            <div className="flex flex-col gap-3 min-w-[160px]">
              <span className="text-sm font-semibold text-gray-placeholder dark:text-gray-placeholder-dk uppercase tracking-wide">
                Saved Views
              </span>
              <div className="flex flex-col gap-[6px]">
                {(() => {
                  const isSelected = selectedViewId === "all_leads";
                  return (
                    <button
                      onClick={() => handleSelectView("all_leads")}
                      className={`flex items-center justify-between w-full py-[5px] px-2 rounded-[8px] cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border border-selected-option-highlight dark:border-selected-option-highlight-dk"
                          : "border border-transparent hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <CompletedIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
                        <span
                          className={`text-md whitespace-nowrap ${
                            isSelected
                              ? "font-semibold text-verba-blue dark:text-verba-blue-dk"
                              : "font-normal text-verba-indigo dark:text-verba-indigo-dk"
                          }`}
                        >
                          All Leads
                        </span>
                      </div>
                      {isSelected && (
                        <CheckIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
                      )}
                    </button>
                  );
                })()}
              </div>
            </div>

            {crmViews?.length > 0 && (
              <>
                <div className="w-px bg-border-gray dark:bg-border-gray-dk self-stretch" />

                <div className="flex flex-col gap-3 min-w-[186px]">
                  <span className="text-sm font-semibold text-gray-placeholder dark:text-gray-placeholder-dk uppercase tracking-wide">
                    Created
                  </span>
                  <div
                    className={`flex flex-col gap-[6px] max-h-[200px] overflow-y-auto overflow-x-hidden ${visibleScrollbar(
                      "[&::-webkit-scrollbar]:w-[8px]"
                    )}`}
                  >
                    {crmViews?.map((view) => {
                      const isSelected = selectedViewId === view?.id;
                      return (
                        <button
                          key={view?.id}
                          onClick={() => handleSelectView(view?.id)}
                          className={`flex items-center justify-between w-full py-[5px] px-2 rounded-[8px] cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border border-selected-option-highlight dark:border-selected-option-highlight-dk"
                              : "border border-transparent hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                          }`}
                        >
                          <div className="flex items-center gap-1 min-w-0">
                            {view?.emoji_icon && (
                              <span className="text-2xl leading-none shrink-0">
                                {view.emoji_icon}
                              </span>
                            )}
                            <TruncatedTextTooltip
                              text={view?.name}
                              maxWidth="max-w-[150px]"
                              className={`text-md ${
                                isSelected
                                  ? "font-semibold text-verba-blue dark:text-verba-blue-dk"
                                  : "font-normal text-verba-indigo dark:text-verba-indigo-dk"
                              }`}
                            />
                          </div>
                          {isSelected && (
                            <CheckIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="w-full h-px bg-border-gray dark:bg-border-gray-dk" />

          <button
            className="flex items-center gap-1 cursor-pointer group"
            onClick={() => {
              if (onCreateView) {
                onCreateView();
              } else {
                dispatch(setDrawer({ open: true, type: "crm_view" }));
              }
              setIsOpen(false);
            }}
          >
            <PlusFilledIcon className="w-[18px] h-[18px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
            <span className="text-md font-medium text-verba-blue dark:text-verba-blue-dk group-hover:underline">
              Create New View
            </span>
          </button>
        </div>
      )}
    </div>
  );
};
